import { useEffect, useState } from "react";
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc,
  serverTimestamp, getDoc
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Zap, ZapOff, Key, RefreshCw, Copy, ExternalLink, Radio } from "lucide-react";
import { toast } from "sonner";

function genStreamKey() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function DashboardLive() {
  const { user } = useAuth();
  const [station, setStation] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [streamUrl, setStreamUrl] = useState("");
  const [sessionTitle, setSessionTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [going, setGoing] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    try {
      // Load creator's station
      const stationsSnap = await getDocs(query(collection(db, "stations"), where("creatorId", "==", user.uid)));
      if (stationsSnap.empty) { setLoading(false); return; }
      const stationData = { id: stationsSnap.docs[0].id, ...stationsSnap.docs[0].data() };
      setStation(stationData);

      // Check for active live session
      const sessionsSnap = await getDocs(
        query(collection(db, "liveSessions"), where("stationId", "==", stationData.id), where("status", "==", "active"))
      );
      if (!sessionsSnap.empty) {
        setActiveSession({ id: sessionsSnap.docs[0].id, ...sessionsSnap.docs[0].data() });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateStreamKey = async () => {
    if (!station) return;
    const key = genStreamKey();
    await updateDoc(doc(db, "stations", station.id), { streamKey: key });
    setStation((s) => ({ ...s, streamKey: key }));
    toast.success("New stream key generated!");
  };

  const copyKey = () => {
    navigator.clipboard.writeText(station?.streamKey || "");
    toast.success("Copied to clipboard!");
  };

  const goLive = async () => {
    if (!station) return;
    if (!streamUrl.trim()) { toast.error("Paste your stream URL first"); return; }
    setGoing(true);
    try {
      const sessionRef = await addDoc(collection(db, "liveSessions"), {
        creatorId: user.uid,
        stationId: station.id,
        stationName: station.name,
        streamUrl: streamUrl.trim(),
        title: sessionTitle.trim() || `${station.name} — Live`,
        status: "active",
        startedAt: serverTimestamp(),
        endedAt: null,
        viewerCount: 0,
      });
      await updateDoc(doc(db, "stations", station.id), { isLive: true, liveSessionId: sessionRef.id });
      setActiveSession({
        id: sessionRef.id,
        streamUrl: streamUrl.trim(),
        title: sessionTitle.trim() || `${station.name} — Live`,
        status: "active",
      });
      setStation((s) => ({ ...s, isLive: true }));
      toast.success("You're live! Station is now broadcasting.");
    } catch (e) {
      toast.error("Failed to start session");
    }
    setGoing(false);
  };

  const endLive = async () => {
    if (!activeSession || !station) return;
    setEnding(true);
    try {
      await updateDoc(doc(db, "liveSessions", activeSession.id), { status: "ended", endedAt: serverTimestamp() });
      await updateDoc(doc(db, "stations", station.id), { isLive: false, liveSessionId: null });
      setActiveSession(null);
      setStation((s) => ({ ...s, isLive: false }));
      setStreamUrl("");
      setSessionTitle("");
      toast.success("Live session ended.");
    } catch (e) {
      toast.error("Failed to end session");
    }
    setEnding(false);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" /></div>;

  if (!station) return (
    <div className="text-center py-16 text-zinc-500">
      <Radio size={32} className="mx-auto mb-3 opacity-30" />
      <p className="text-sm">You need a station to go live.</p>
      <a href="/dashboard/station" className="text-[#00ff77] text-sm mt-2 inline-block hover:underline">Create your station →</a>
    </div>
  );

  return (
    <div data-testid="dashboard-live" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Zap size={18} className="text-[#00ff77]" /> Go Live
        </h2>
        {station.isLive && (
          <span className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse">
            <span className="w-2 h-2 bg-white rounded-full" /> LIVE
          </span>
        )}
      </div>

      {/* Station Info */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-4 flex items-center gap-3">
        {station.logoURL && <img src={station.logoURL} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />}
        <div>
          <p className="text-white font-semibold">{station.name}</p>
          <p className="text-zinc-500 text-xs">{station.genre || "Radio Station"}</p>
        </div>
      </div>

      {/* Stream Key */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Key size={15} className="text-[#B026FF]" />
          <h3 className="text-white font-bold text-sm">Your Stream Key</h3>
        </div>
        <p className="text-zinc-500 text-xs leading-relaxed">
          Use this key when setting up OBS or your streaming software. Stream to your preferred platform (YouTube Live, Twitch, etc.), then paste the resulting stream URL below to activate your StagePass live session.
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-zinc-400 text-xs font-mono truncate">
            {station.streamKey || <span className="text-zinc-600 italic">No key generated yet</span>}
          </div>
          <button onClick={copyKey} disabled={!station.streamKey} data-testid="copy-stream-key-btn"
            className="p-2 rounded-xl bg-[#1a1a1a] hover:bg-[#B026FF]/20 border border-white/10 hover:border-[#B026FF]/30 text-zinc-400 hover:text-[#B026FF] transition-colors disabled:opacity-40">
            <Copy size={14} />
          </button>
          <button onClick={generateStreamKey} data-testid="gen-stream-key-btn"
            className="p-2 rounded-xl bg-[#1a1a1a] hover:bg-white/5 border border-white/10 text-zinc-400 hover:text-white transition-colors" title="Generate new key">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Active Session or Go Live form */}
      {activeSession ? (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <h3 className="text-white font-bold text-sm">Live Now — {activeSession.title}</h3>
          </div>
          {activeSession.streamUrl && (
            <a href={activeSession.streamUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#00ff77] text-xs hover:underline">
              <ExternalLink size={12} /> {activeSession.streamUrl}
            </a>
          )}
          <button onClick={endLive} disabled={ending} data-testid="end-live-btn"
            className="flex items-center gap-2 bg-red-500/20 border border-red-500/30 text-red-400 font-bold text-sm px-5 py-2.5 rounded-full hover:bg-red-500/30 transition-colors disabled:opacity-50">
            <ZapOff size={14} /> {ending ? "Ending..." : "End Live Session"}
          </button>
        </div>
      ) : (
        <div className="bg-[#111] border border-[#00ff77]/10 rounded-2xl p-5 space-y-4">
          <h3 className="text-white font-bold text-sm flex items-center gap-2">
            <Zap size={15} className="text-[#00ff77]" /> Start a Live Session
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Session title (optional)</label>
              <input
                type="text"
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                placeholder={`${station.name} — Live`}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00ff77]/50"
              />
            </div>
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Stream embed URL <span className="text-red-400">*</span></label>
              <input
                type="url"
                value={streamUrl}
                onChange={(e) => setStreamUrl(e.target.value)}
                placeholder="https://youtube.com/embed/... or HLS stream URL"
                data-testid="stream-url-input"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#00ff77]/50"
              />
              <p className="text-zinc-600 text-[10px] mt-1">
                Go live on YouTube/Twitch first, copy the embed URL, then paste it here.
              </p>
            </div>
          </div>
          <button onClick={goLive} disabled={going || !streamUrl.trim()} data-testid="go-live-btn"
            className="flex items-center gap-2 bg-[#00ff77] text-black font-bold text-sm px-6 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors disabled:opacity-50">
            <Zap size={14} /> {going ? "Starting..." : "Go Live"}
          </button>
        </div>
      )}

      {/* OBS Setup Guide */}
      <div className="bg-[#0d0d0d] border border-white/5 rounded-2xl p-5 space-y-2">
        <h3 className="text-zinc-400 font-semibold text-xs uppercase tracking-widest">OBS Setup Guide</h3>
        <ol className="text-zinc-500 text-xs space-y-2 list-decimal list-inside leading-relaxed">
          <li>Open OBS → Settings → Stream</li>
          <li>Service: YouTube / Twitch / Custom RTMP</li>
          <li>Server: <span className="text-zinc-300 font-mono">rtmp://a.rtmp.youtube.com/live2</span></li>
          <li>Stream Key: Use your YouTube/Twitch stream key (or the StagePass key above)</li>
          <li>Click "Start Streaming" in OBS</li>
          <li>Wait for your platform to show the live preview, copy the embed URL</li>
          <li>Paste the embed URL above and click <strong className="text-[#00ff77]">Go Live</strong></li>
        </ol>
      </div>
    </div>
  );
}
